import React from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import ProductCard from '../components/Collections/ProductCard';
import {
  categoryLabels,
  isCategory,
  productsByCategory,
} from '../data/products';

const Section = styled.section`
  padding: 8rem 0 6rem;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const Breadcrumb = styled.nav`
  font-family: ${({ theme }) => theme.fonts.accent};
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.lightText};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  letter-spacing: 0.05em;

  a {
    color: ${({ theme }) => theme.colors.lightText};

    &:hover {
      color: ${({ theme }) => theme.colors.text};
    }
  }

  span[aria-current] {
    color: ${({ theme }) => theme.colors.text};
  }
`;

const Heading = styled.h1`
  font-size: 2.5rem;
  margin-bottom: ${({ theme }) => theme.spacing.xxl};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
`;

const Empty = styled.p`
  color: ${({ theme }) => theme.colors.lightText};
`;

const CollectionCategory: React.FC = () => {
  const { category } = useParams<{ category: string }>();

  if (!isCategory(category)) {
    return (
      <Section>
        <Container>
          <Heading>Not found</Heading>
          <Empty>
            That collection doesn't exist. <Link to="/collections">Back to collections</Link>.
          </Empty>
        </Container>
      </Section>
    );
  }

  const items = productsByCategory(category);
  const label = categoryLabels[category];

  return (
    <Section>
      <Container>
        <Breadcrumb aria-label="Breadcrumb">
          <Link to="/collections">Collections</Link> / <span aria-current="page">{label}</span>
        </Breadcrumb>
        <Heading>{label}</Heading>
        {items.length === 0 ? (
          <Empty>New pieces are on the way.</Empty>
        ) : (
          <Grid>
            {items.map((product) => (
              <ProductCard key={product.slug} product={product} />
            ))}
          </Grid>
        )}
      </Container>
    </Section>
  );
};

export default CollectionCategory;
